import { Location } from '@/api/types/Location';
import { Card, CardContent, IconButton, Stack, Typography, useTheme } from '@mui/material';
import ManageLocationsDialog from './ManageLocationsDialog';
import { useContext, useState, forwardRef } from 'react';
import { ActivityContext } from '@/contexts/ActivityContext';
import { Room } from '@mui/icons-material';

type LocationCardProps = {
  location: Location;
  icon?: JSX.Element;
  onIconClick?: () => void;
  onSelect?: () => void;
  isClickable: boolean;
  ariaLabel?: string;
  tabIndex?: number;
};

const LocationCard = forwardRef<HTMLDivElement, LocationCardProps>(
  ({ location, icon, onIconClick, onSelect, isClickable, ariaLabel, tabIndex = 0 }, ref) => {
    const { palette } = useTheme();
    const { activity } = useContext(ActivityContext);

    const [handleLocationDialog, setHandleLocationDialog] = useState(false);

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (
        isClickable &&
        (event.key === 'Enter' || event.key === ' ') &&
        event.currentTarget === event.target
      ) {
        event.preventDefault();
        onSelect?.();
      }
    };

    const handleCardClick = (event: React.MouseEvent) => {
      if (isClickable) {
        onSelect?.();
      }
    };

    const handleEditClick = (event: React.MouseEvent) => {
      event.stopPropagation();
      if (onIconClick) {
        onIconClick();
      } else {
        setHandleLocationDialog(true);
      }
    };

    return (
      <>
        <div ref={ref} role="group" aria-labelledby={`location-name-${location.locationId}`}>
          <Card
            sx={{
              backgroundColor: palette.background.paper,
              border: '1px solid lightgray',
              borderLeft: `10px solid ${location.color ? location.color : palette.grey[500]}`,
              borderRadius: '8px',
              boxShadow: 1,
              transition: '0.2s',
              position: 'relative',
            }}
          >
            <CardContent sx={{ py: 1.6, px: 0.5, '&:last-child': { pb: 1.6 } }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  px={0.2}
                  flex={1}
                  minWidth={0}
                  component={isClickable ? 'button' : 'div'}
                  onClick={isClickable ? handleCardClick : undefined}
                  onKeyDown={handleKeyDown}
                  tabIndex={isClickable ? tabIndex : -1}
                  sx={{
                    background: 'none',
                    border: 'none',
                    textAlign: 'left',
                    cursor: isClickable ? 'pointer' : 'default',
                    p: 0,
                    width: '100%',
                    '&:hover': isClickable
                      ? {
                          backgroundColor: palette.action.hover,
                        }
                      : undefined,
                    '&:focus': isClickable
                      ? {
                          outline: `2px solid ${palette.primary.main}`,
                        }
                      : undefined,
                  }}
                  aria-label={isClickable ? `Select location: ${location.locationName}` : undefined}
                >
                  <Room color="primary" sx={{ flexShrink: 0, fontSize: '2rem' }} />
                  <Stack spacing={0}>
                    <Typography
                      id={`location-name-${location.locationId}`}
                      variant="h6"
                      fontWeight={'bold'}
                      color={palette.primary.main}
                      component="span"
                      sx={{
                        m: 0,
                        lineHeight: 1.2,
                        overflowWrap: 'break-word',
                        wordBreak: 'break-word',
                      }}
                    >
                      {location.locationName}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={80}
                      component="span"
                      sx={{
                        textTransform: 'capitalize',
                        m: 0,
                        lineHeight: 1.2,
                        overflowWrap: 'break-word',
                        wordBreak: 'break-word',
                      }}
                      color={palette.primary.main}
                    >
                      {location.locationAddress}
                    </Typography>
                  </Stack>
                </Stack>

                {(activity || !isClickable) && icon && (
                  <IconButton
                    color="primary"
                    sx={{ flexShrink: 0, alignSelf: 'flex-start', ml: 1 }}
                    onClick={handleEditClick}
                    aria-label={ariaLabel || `Edit location: ${location.locationName}`}
                    tabIndex={0}
                  >
                    {icon}
                  </IconButton>
                )}
              </Stack>
            </CardContent>
          </Card>
        </div>
        <ManageLocationsDialog
          location={location}
          open={handleLocationDialog}
          onClose={() => {
            setHandleLocationDialog(false);
          }}
        />
      </>
    );
  },
);

LocationCard.displayName = 'LocationCard';

export default LocationCard;
